import React, { useState, useEffect, useRef } from 'react';
import StatsPiCard from './StatsPiCard';
import { MdOutlineAccessTimeFilled, MdOutlineError, MdCancel } from 'react-icons/md';
import { FaFile, FaMoneyBillWave } from 'react-icons/fa';
import { toast } from 'sonner';
import { makeRESTApiRequest, ABORT_ERROR } from '../../../helpers/api';
import { ADMIN_ROLE, ENDPOINTS, ORDER_STATUS_RANGE, SALES_PERSON_ROLE } from '../../../helpers/constants';
import Loader from '../../Loader';
import { getChainOrganizationLinkFilter, isClient, isStaff } from '../../../helpers/global';
import { ArrowUpRightCircle, CalendarCheck } from 'react-bootstrap-icons';
import { useAdvancedDateRangePicker } from '../../../hooks/useAdvancedDateRangePicker';
import moment from 'moment';
import { Building2 } from 'lucide-react';

export default function StatsCardsSection({
  organizationIds,
  children,
  currentUserInfo,
  globalDateRange,
  beforePipelineSlot = null,
  pipelineSlot = null,
  cardGap = 12
}) {
  const [dashboardstatsDate, setDashboardstatsDate] = useState(null);
  const { dateRange, setDateRange, handleNavigateDate } = useAdvancedDateRangePicker(globalDateRange);
  const userRole = localStorage.getItem('user-role');
  const isStaffRole = isStaff(userRole);
  const [loading, setLoading] = useState(true);
  const abortControllerRef = useRef(null);
  
  useEffect(() => {
    if (globalDateRange) {
      setDateRange(globalDateRange);
    }
  }, [globalDateRange]);

  useEffect(() => {
    const getOrgdashboardstatsDate = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setLoading(true);
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
         // Ensure startDate is at start of day and endDate is at end of day
         const startDate = dateRange.startDate ? moment(dateRange.startDate).startOf('day') : null;
         const endDate = dateRange.endDate ? moment(dateRange.endDate).endOf('day') : null;

        // Fetch all dashboard stats in parallel
        const [statsResponse, closedLastMonthResponse, closedThisMonthResponse] = await Promise.all([
          makeRESTApiRequest({
            endpoint: ENDPOINTS.ORG_DASHBOARD_STATS,
            method: 'POST',
            requestBody: {
              organizationIds: organizationIds,
              dateFilter: {
                startDate: startDate?.toISOString() || null,
                endDate: endDate?.toISOString() || null
              }
            },
            signal: controller.signal
          }),
          makeRESTApiRequest({
            endpoint: ENDPOINTS.ORG_DASHBOARD_CLOSED_FILES_LAST_MONTH,
            method: 'POST',
            requestBody: {
              organizationIds: organizationIds
            },
            signal: controller.signal
          }),
          makeRESTApiRequest({
            endpoint: ENDPOINTS.ORG_DASHBOARD_CLOSED_FILES_THIS_MONTH,
            method: 'POST',
            requestBody: {
              organizationIds: organizationIds
            },
            signal: controller.signal
          })
        ]);

        if (!controller.signal.aborted) {
          if (statsResponse.error && statsResponse.error !== ABORT_ERROR) {
            toast.error(statsResponse.error);
          }
          if (closedLastMonthResponse.error && closedLastMonthResponse.error !== ABORT_ERROR) {
            console.error('Error fetching closed files last month:', closedLastMonthResponse.error);
          }
          if (closedThisMonthResponse.error && closedThisMonthResponse.error !== ABORT_ERROR) {
            console.error('Error fetching closed files this month:', closedThisMonthResponse.error);
          }

          if (statsResponse.response) {
            const combinedData = {
              ...statsResponse.response.data,
              closedFilesLastMonthOrdersSummary: closedLastMonthResponse.response?.data || { totalCount: 0, group: [] },
              closedFilesThisMonthOrdersSummary: closedThisMonthResponse.response?.data || { totalCount: 0, group: [] }
            };
            setDashboardstatsDate(combinedData);
          }
          setLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Error fetching dashboard stats:', error);
          toast.error('Failed to load dashboard stats');
          setLoading(false);
        }
      }
    };

    getOrgdashboardstatsDate();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [organizationIds.length, dateRange]);

  const iconSize = 14;

  const iconColors = {
    new: '#0056b3',
    inProgress: '#cc8400',
    unpaid: '#8B5CF6',
    overdue: '#dc3545',
    paid: '#10B981',
    cancelled: '#dc3545'
  };

  const iconWrapperBgColor = '#e9eff6';

  const isAdmin = currentUserInfo?.role === ADMIN_ROLE;
  const isClientUser = isClient(currentUserInfo?.role);
  const cardsHiddenForAdmin = new Set(['ordersInProgress', 'paidLastMonth', 'paidThisMonth']);
  const cardsHiddenForClient = new Set(['paidLastMonth', 'paidThisMonth']);

  const cardsData = [
    {
      cardId: 'ordersInProgress',
      title: (
        <div className="d-flex flex-column cursor-pointer" onClick={() => window.open(`/services/pipeline`, '_blank')}>
          <div className="d-flex align-items-center">
            <span>Orders In Progress</span>
            <ArrowUpRightCircle className="cursor-pointer ml-2" size={14} />
          </div>
        </div>
      ),
      totalCount: dashboardstatsDate?.ordersInProgress?.totalCount || 0,
      groupData: dashboardstatsDate?.ordersInProgress?.group || [],
      icon: (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '8px',
            backgroundColor: iconWrapperBgColor
          }}
        >
          <MdOutlineAccessTimeFilled size={iconSize} style={{ color: iconColors.inProgress }} />
        </div>
      )
    },
    {
      title: (
        <div
          className="d-flex flex-column cursor-pointer"
          onClick={() =>
            window.open(
              `/services/pipeline?orderStatus=Pending To Pay${getChainOrganizationLinkFilter(
                organizationIds,
                isStaffRole
              )}&range=${isClientUser ? ORDER_STATUS_RANGE.AWAITING_CLOSING : ORDER_STATUS_RANGE.LAST30DAYS}`,
              '_blank'
            )
          }
        >
          <div className="d-flex align-items-center">
            <span>{isClientUser ? 'Completed Orders – Unpaid' : 'Delivered orders'}</span>
            <ArrowUpRightCircle className="cursor-pointer ml-2" size={14} />
          </div>

          <span
            style={{
              fontSize: '12px',
              lineHeight: '1',
              marginTop: '-4px',
              display: 'inline-block',
              color: 'blue',
              opacity: 0.5,
              marginTop: '1px'
            }}
          >
            {isClientUser ? 'Awaiting Closing' : 'pending payment\u00a0 (of last 30 days)'}
          </span>
        </div>
      ),
      totalCount: isClientUser
        ? dashboardstatsDate?.awaitingClosingOrdersSummary?.totalCount || 0
        : dashboardstatsDate?.last30daysPendingToPayOrderSummary?.totalCount || 0,
      groupData: isClientUser
        ? dashboardstatsDate?.awaitingClosingOrdersSummary?.group || []
        : dashboardstatsDate?.last30daysPendingToPayOrderSummary?.group || [],
      icon: (
        <div
          className="d-flex align-items-center justify-content-center "
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '8px',
            backgroundColor: iconWrapperBgColor
          }}
        >
          <FaMoneyBillWave size={iconSize} style={{ color: iconColors.unpaid }} />
        </div>
      )
    },
    {
      title: (
        <div
          className="d-flex flex-column cursor-pointer"
          onClick={() =>
            window.open(
              `/services/pipeline?orderStatus=Pending To Pay${getChainOrganizationLinkFilter(
                organizationIds,
                isStaffRole
              )}&range=${isClientUser ? ORDER_STATUS_RANGE.PAST_CLOSING_DATE : ORDER_STATUS_RANGE.MORETHAN30DAYS}`,
              '_blank'
            )
          }
        >
          <div className="d-flex align-items-center">
            <span>Overdue Orders</span>
            <ArrowUpRightCircle className="cursor-pointer ml-2" size={14} />
          </div>
          <span
            style={{
              fontSize: '12px',
              lineHeight: '1',
              marginTop: '-4px',
              display: 'inline-block',
              color: 'red',
              opacity: 0.5,
              marginTop: '1px'
            }}
          >
            {isClientUser ? 'Past Closing Date' : 'Delivered orders unpaid\u00a0 (over 30 days)'}
          </span>
        </div>
      ),
      totalCount: isClientUser
        ? dashboardstatsDate?.overdueByClosingDateOrdersSummary?.totalCount || 0
        : dashboardstatsDate?.overdueOrdersSummary?.totalCount || 0,
      groupData: isClientUser
        ? dashboardstatsDate?.overdueByClosingDateOrdersSummary?.group || []
        : dashboardstatsDate?.overdueOrdersSummary?.group || [],
      icon: (
        <div
          className="d-flex align-items-center justify-content-center "
          style={{ width: '22px', height: '22px', borderRadius: '8px', backgroundColor: iconWrapperBgColor }}
        >
          <MdOutlineError size={iconSize} style={{ color: iconColors.overdue }} />
        </div>
      )
    },
    {
      cardId: 'paidLastMonth',
      title: (
        <div
          className="d-flex flex-column cursor-pointer"
          onClick={() =>
            window.open(
              `/services/pipeline?orderStatus=Paid${getChainOrganizationLinkFilter(
                organizationIds,
                isStaffRole
              )}&range=${ORDER_STATUS_RANGE.LASTMONTH}`,
              '_blank'
            )
          }
        >
          <div className="d-flex align-items-center">
            <span>Paid Orders Last Month</span>
            <ArrowUpRightCircle className="cursor-pointer ml-2" size={14} />
          </div>
        </div>
      ),
      totalCount: dashboardstatsDate?.closedFilesLastMonthOrdersSummary?.totalCount || 0,
      groupData: dashboardstatsDate?.closedFilesLastMonthOrdersSummary?.group || [],
      icon: (
        <div
          className="d-flex align-items-center justify-content-center "
          style={{ width: '22px', height: '22px', borderRadius: '8px', backgroundColor: iconWrapperBgColor }}
        >
          <FaFile size={iconSize} style={{ color: '#10B981' }} />
        </div>
      )
    },
    {
      cardId: 'paidThisMonth',
      title: (
        <div
          className="d-flex flex-column cursor-pointer"
          onClick={() =>
            window.open(
              `/services/pipeline?orderStatus=Paid${getChainOrganizationLinkFilter(
                organizationIds,
                isStaffRole
              )}&range=${ORDER_STATUS_RANGE.THISMONTH}`,
              '_blank'
            )
          }
        >
          <div className="d-flex align-items-center">
            <span>Paid Orders This Month</span>
            <ArrowUpRightCircle className="cursor-pointer ml-2" size={14} />
          </div>
        </div>
      ),
      totalCount: dashboardstatsDate?.closedFilesThisMonthOrdersSummary?.totalCount || 0,
      groupData: dashboardstatsDate?.closedFilesThisMonthOrdersSummary?.group || [],
      icon: (
        <div
          className="d-flex align-items-center justify-content-center "
          style={{ width: '22px', height: '22px', borderRadius: '8px', backgroundColor: iconWrapperBgColor }}
        >
          <FaFile size={iconSize} style={{ color: '#10B981' }} />
        </div>
      )
    },
    {
      title: (
        <div
          className="d-flex flex-column cursor-pointer"
          onClick={() =>
            window.open(
              `/services/pipeline?orderStatus=Cancelled&orderStatus=Refunded&orderStatus=Refund Requested${getChainOrganizationLinkFilter(
                organizationIds,
                isStaffRole
              )}&range=${ORDER_STATUS_RANGE.LAST60DAYS}`,
              '_blank'
            )
          }
        >
          <div className="d-flex align-items-center">
            <span>Cancelled orders</span>
            <ArrowUpRightCircle className="cursor-pointer ml-2" size={14} />
          </div>

          <span
            style={{
              fontSize: '12px',
              lineHeight: '1',
              marginTop: '-4px',
              display: 'inline-block',
              opacity: 0.5,
              color: 'red',
              marginTop: '1px'
            }}
          >
            (of last 60 days)
          </span>
        </div>
      ),
      totalCount: dashboardstatsDate?.totalCancelledOrdersSummary?.totalCount || 0,
      cancellationRate: dashboardstatsDate?.totalCancelledOrdersSummary?.cancellationRate || 0,
      groupData: dashboardstatsDate?.totalCancelledOrdersSummary?.group || [],
      type: 'percentage',
      icon: (
        <div
          className="d-flex align-items-center justify-content-center "
          style={{ width: '22px', height: '22px', borderRadius: '8px', backgroundColor: iconWrapperBgColor }}
        >
          <MdCancel size={iconSize} style={{ color: iconColors.cancelled }} />
        </div>
      )
    }
  ];

  return (
    <>
      <div className="d-flex flex-row flex-lg-row flex-column align-items-start align-items-lg-center justify-content-between my-2">
        {currentUserInfo?.role === SALES_PERSON_ROLE ? (
          <div
            className="fw-bold text-muted ml-2 xxmidFont d-flex align-items-center bg-grey rounded-pill"
            style={{ padding: '6px 12px' }}
          >
            <Building2 size={16} className="mr-1 my-1" />{' '}
            <span className="text-dark mr-1">{currentUserInfo?.organization?.length}</span>{' '}
            {currentUserInfo?.organization?.length > 1 ? 'Organizations' : 'Organization'}{' '}
          </div>
        ) : (
          ''
        )}

        <div className="mb-2 mt-2 mb-lg-0 d-flex align-items-end flex-shrink-0">
          {currentUserInfo?.role === ADMIN_ROLE && (
            <span className="h3 fw-bold mb-0" style={{ letterSpacing: '-0.025em' }}>
              Admin Dashboard
            </span>
          )}
      </div>
        {children ? (
          <div>{children}</div>
        ) : (
          <p className="text-secondary mb-0 font-weight-bold d-flex align-items-center" style={{ fontSize: '1.05rem' }}>
            <CalendarCheck className="mx-1 text-success" />
            <span className="fw-bold mx-1">Customer Since</span>
            <span className="fw-bold text-info">
              {loading ? (
                <span className="">Loading...</span>
              ) : (
                new Date(dashboardstatsDate?.firstOrderDate).getFullYear()
              )}
            </span>
          </p>
        )}
      </div>
      {beforePipelineSlot}
      {pipelineSlot}
      {loading ? (
        <Loader />
      ) : (
        <div
          className="row"
          style={{
            marginLeft: `-${cardGap / 2}px`,
            marginRight: `-${cardGap / 2}px`,
            rowGap: `${cardGap}px`
          }}
        >
          {cardsData
            ?.filter((card) => {
              if (isAdmin && cardsHiddenForAdmin.has(card.cardId)) return false;
              if (isClientUser && cardsHiddenForClient.has(card.cardId)) return false;
              return true;
            })
            .map(({ cardId, ...cardProps }, index) => (
              <StatsPiCard
                key={index}
                title={cardProps.title}
                totalCount={cardProps.totalCount}
                groupData={cardProps.groupData}
                icon={cardProps.icon}
                type={cardProps.type}
                cancellationRate={cardProps.cancellationRate}
                columnClass={isClientUser ? 'col-12 col-sm-6 col-xl-3 mb-0' : 'col-12 col-md-4 mb-0'}
                columnStyle={{ paddingLeft: cardGap / 2, paddingRight: cardGap / 2 }}
              />
            ))}
        </div>
      )}
    </>
  );
}
